import React from "react";
import { Link } from "react-router-dom";
import {
  HandHeart,
  Heart,
  Compass,
  Tree,
  ArrowRight,
  Gift,
  MusicNotes,
  Path,
  BookOpen,
  Quotes,
  Sun,
} from "@phosphor-icons/react";
import { IMAGES } from "../lib/site";
import { AUTHOR } from "../data/books";
import SectionHeading from "../components/SectionHeading";
import Seo from "../components/Seo";

const About = () => {
  return (
    <div data-testid="page-about">
      <Seo
        title="About Chaplain Derrick Hall & Our Ministry"
        description="Meet Chaplain Derrick Hall, founder of Begin Again Living Ministries — a chaplain, author, and grief companion serving families through loss since 2006."
      />
      {/* -------------- HERO -------------- */}
      <section className="pt-20 sm:pt-28 pb-14 sm:pb-20">
        <div className="container-x grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 reveal">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary font-semibold">
              <span className="w-8 h-px bg-secondary" />
              About the ministry
            </span>
            <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.05] text-ink-900">
              A gentle ministry for life&rsquo;s hardest seasons.
            </h1>
            <p className="mt-6 text-ink-700 text-lg leading-relaxed">
              Begin Again Living Ministries exists to walk alongside individuals
              and families navigating grief, caregiving, serious illness,
              hospice, spiritual discouragement, and life&rsquo;s difficult
              transitions.
            </p>
            <p className="mt-5 text-ink-700 text-base sm:text-lg leading-relaxed">
              We believe beginning again does not always mean starting over.
              Sometimes it means discovering how to live forward when life has
              changed.
            </p>
            <p className="mt-5 text-ink-700 text-base sm:text-lg leading-relaxed">
              Through compassionate support, spiritual care, education,
              practical resources, and meaningful conversation, BALM helps
              people find hope, understanding, and a way forward &mdash; one
              step at a time.
            </p>
          </div>
          <div className="lg:col-span-6 reveal">
            <div className="relative">
              <div
                aria-hidden="true"
                className="absolute -inset-6 bg-gradient-to-br from-secondary/25 via-primary/10 to-transparent rounded-3xl blur-2xl"
              />
              <img
                src={IMAGES.reflection}
                alt="Quiet reflection by the water"
                className="relative rounded-2xl shadow-xl border border-ink-100 w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* -------------- OUR BEGINNING -------------- */}
      <section className="py-20 sm:py-28 bg-paper border-y border-ink-100" data-testid="about-beginning">
        <div className="container-x">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <SectionHeading
                overline="Our beginning"
                title="It started with a gift — and a decision to give back."
              />
              <div className="mt-8 flex items-center gap-3 text-ink-500 text-sm">
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-softblue text-primary">
                  <MusicNotes size={18} weight="duotone" />
                </span>
                <span>Music. Ministry. A dream carried patiently.</span>
              </div>
            </div>

            <div className="lg:col-span-7 space-y-6 text-ink-700 text-base sm:text-lg leading-relaxed">
              <p>
                The vision for Begin Again Living Ministries began years before
                the organization itself.
              </p>
              <p>
                In{" "}
                <span className="font-semibold text-ink-900">1997</span>,
                Derrick Hall began carrying a vision for a ministry dedicated
                to helping and supporting people through difficult seasons of
                life. For years, that vision remained something he hoped one
                day to bring to life.
              </p>
              <p>
                Then, in{" "}
                <span className="font-semibold text-ink-900">2005</span>, while
                performing at a church concert, Derrick and his family were
                invited to lunch. During that gathering, he was unexpectedly
                presented with a check for{" "}
                <span className="font-semibold text-ink-900">$2,000</span>.
              </p>
              <p>
                Derrick could have treated the money simply as compensation for
                his music or used it for something personal. Instead, he
                remembered the ministry vision he had been carrying since 1997.
              </p>

              {/* Callout — "That $2,000 became the seed." */}
              <div
                data-testid="about-seed-callout"
                className="relative mt-8 rounded-2xl border border-secondary/30 bg-gradient-to-br from-secondary/10 via-white to-primary/5 p-7 sm:p-9 shadow-[0_10px_40px_-15px_rgba(15,23,42,0.15)]"
              >
                <span
                  aria-hidden="true"
                  className="absolute -top-4 left-7 inline-flex items-center justify-center w-9 h-9 rounded-full bg-secondary text-white shadow-md"
                >
                  <Gift size={18} weight="duotone" />
                </span>
                <p className="font-heading text-2xl sm:text-3xl font-medium text-ink-900 leading-snug">
                  That $2,000 became the seed
                  <span className="text-primary"> that helped turn the vision into reality.</span>
                </p>
                <p className="mt-4 text-ink-700 leading-relaxed">
                  In{" "}
                  <span className="font-semibold text-primary">
                    February 2006
                  </span>
                  , Derrick filed to establish Begin Again Living Ministries as
                  a nonprofit.
                </p>
                <p className="mt-3 text-ink-700 leading-relaxed italic">
                  What began as a vision years earlier had now become a
                  ministry.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* -------------- FROM MEETING NEEDS TO... -------------- */}
      <section className="py-20 sm:py-28" data-testid="about-evolution">
        <div className="container-x max-w-4xl">
          <div className="text-center">
            <SectionHeading
              overline="How the ministry grew"
              title="From meeting needs to walking through life's hardest seasons."
              align="center"
            />
            <p className="mt-6 text-ink-700 text-lg leading-relaxed italic max-w-2xl mx-auto">
              The mission grew, but the heart remained the same.
            </p>
          </div>

          {/* Subtle milestone rhythm — 1997 · 2006 · Today */}
          <div
            aria-hidden="true"
            className="mt-14 flex items-center justify-center gap-4 sm:gap-8"
          >
            {[
              { year: "1997", label: "Vision begins" },
              { year: "2006", label: "Founded" },
              { year: "Today", label: "Whole-life care" },
            ].map((m, i) => (
              <React.Fragment key={m.year}>
                {i > 0 && (
                  <span className="h-px w-8 sm:w-16 bg-gradient-to-r from-secondary/40 via-secondary/70 to-secondary/40" />
                )}
                <div className="text-center">
                  <div className="font-heading text-xl sm:text-2xl font-medium text-primary">
                    {m.year}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-ink-500 font-semibold mt-1">
                    {m.label}
                  </div>
                </div>
              </React.Fragment>
            ))}
          </div>

          <div className="mt-14 space-y-6 text-ink-700 text-base sm:text-lg leading-relaxed">
            <p>
              In its early years, Begin Again Living Ministries used donations
              to help individuals facing practical hardships &mdash; including
              groceries, utilities, and other immediate needs.
            </p>
            <p>
              As Derrick&rsquo;s work in hospice deepened, he continued
              encountering patients, caregivers, grieving families, and people
              trying to make sense of lives that had suddenly changed. That
              experience helped further shape and focus the ministry&rsquo;s
              work with serious illness, caregiving, hospice, grief, spiritual
              care, and difficult life transitions.
            </p>
            <p className="text-ink-900 font-semibold pt-2">
              And the words <em>Begin Again</em> took on an even deeper
              meaning.
            </p>
          </div>

          <ul className="mt-10 grid sm:grid-cols-2 gap-x-8 gap-y-4 max-w-3xl mx-auto">
            {[
              "finding strength while caring for someone you love.",
              "navigating a serious diagnosis.",
              "learning how to be present during hospice.",
              "waking up after the death of someone you love and wondering how life continues.",
            ].map((line) => (
              <li key={line} className="flex items-start gap-3 text-ink-700">
                <span
                  aria-hidden="true"
                  className="mt-2 h-1.5 w-1.5 rounded-full bg-secondary shrink-0"
                />
                <span>Beginning again could mean {line}</span>
              </li>
            ))}
          </ul>

          <div className="mt-14 pt-10 border-t border-ink-200/70 text-center">
            <p className="text-ink-700 leading-relaxed">
              The ministry&rsquo;s work became more focused, but its original
              purpose never disappeared:
            </p>
            <p className="mt-4 font-heading text-2xl sm:text-3xl text-primary font-medium leading-snug max-w-2xl mx-auto">
              Help people find hope and a way forward when life changes.
            </p>
          </div>
        </div>
      </section>

      {/* -------------- WHAT BEGIN AGAIN MEANS -------------- */}
      <section className="py-20 sm:py-24 bg-softblue/40" data-testid="about-begin-again">
        <div className="container-x max-w-4xl">
          <div className="text-center">
            <Quotes size={36} weight="duotone" className="text-primary/70 mx-auto" />
            <h2 className="mt-5 font-heading text-3xl sm:text-4xl lg:text-5xl font-medium text-ink-900 leading-[1.15]">
              What <span className="text-primary">Begin Again</span> means.
            </h2>
          </div>
          <div className="mt-10 space-y-5 text-ink-700 text-lg leading-relaxed">
            <p>Beginning again does not mean forgetting what happened.</p>
            <p>It does not mean pretending everything is okay.</p>
            <p>
              And it does not mean leaving behind the people or experiences that
              have shaped your life.
            </p>
            <p className="pt-4 border-t border-ink-200/60">
              Sometimes beginning again simply means taking the next step
              &mdash; with support, faith, understanding, and hope.
            </p>
            <p className="text-ink-900 font-semibold">
              That is the space Begin Again Living Ministries seeks to provide.
            </p>
          </div>
        </div>
      </section>

      {/* -------------- WHAT WE BELIEVE -------------- */}
      <section className="py-20 sm:py-28" data-testid="about-beliefs">
        <div className="container-x">
          <SectionHeading
            overline="What we believe"
            title="Compassion led by faith. Care led by listening."
            align="center"
            className="mb-14"
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Heart,
                title: "Compassion First",
                text: "We meet people where they are. We listen before we speak, honor each person's story, and offer compassionate presence without rushing the journey.",
              },
              {
                icon: Compass,
                title: "Christ-Centered Hope",
                text: "Our ministry is rooted in the love of Christ and shaped by grace, prayer, Scripture, compassion, and hope.",
              },
              {
                icon: Tree,
                title: "Whole-Person Care",
                text: "We recognize that difficult seasons affect the emotional, spiritual, relational, and practical dimensions of life. Our approach honors the whole person and the whole story.",
              },
              {
                icon: HandHeart,
                title: "Practical Support",
                text: "Compassion should have hands and feet. Whether through education, resources, spiritual care, workshops, grief support, or community outreach, we seek to turn caring into meaningful action.",
              },
            ].map(({ icon: Icon, title, text }) => (
              <div
                key={title}
                className="group bg-white rounded-2xl p-7 border border-ink-100 shadow-[0_8px_30px_rgba(0,0,0,0.04)] hover:-translate-y-1 hover:shadow-[0_18px_50px_-10px_rgba(15,23,42,0.15)] transition-all duration-300"
                data-testid={`value-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                <span className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-softblue text-primary group-hover:bg-primary group-hover:text-white transition-colors">
                  <Icon size={24} weight="duotone" />
                </span>
                <h3 className="mt-5 font-heading text-xl font-medium text-ink-900">
                  {title}
                </h3>
                <p className="mt-3 text-ink-700 leading-relaxed text-sm">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* -------------- OUR MINISTRY TODAY -------------- */}
      <section
        className="py-20 sm:py-28 bg-paper border-y border-ink-100"
        data-testid="about-today"
      >
        <div className="container-x grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <SectionHeading
              overline="Our ministry today"
              title="The same heart. A wider reach."
            />
            <div className="mt-8 inline-flex items-center gap-3 text-ink-500 text-sm">
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-softblue text-primary">
                <BookOpen size={18} weight="duotone" />
              </span>
              <span>Ministry &middot; Publishing &middot; Education</span>
            </div>
          </div>

          <div className="lg:col-span-7 space-y-6 text-ink-700 leading-relaxed">
            <p>
              Today, Begin Again Living Ministries continues that original
              commitment through compassionate support, grief and caregiver
              resources, spiritual care, educational programs, workshops,
              publishing, and professional training.
            </p>
            <p>
              BALM also serves as the publishing and educational ministry
              through which Derrick Hall&rsquo;s books and educational
              resources are made available, including the{" "}
              <span className="font-semibold text-ink-900">
                From Diagnosis to Goodbye
              </span>{" "}
              Educational Platform &mdash; developed to help individuals,
              families, caregivers, churches, hospice organizations, and
              healthcare professionals navigate serious illness, caregiving,
              hospice, grief, and meaningful conversations with greater
              compassion and understanding.
            </p>

            {/* IP-safe attribution note */}
            <div
              data-testid="about-ip-note"
              className="mt-4 rounded-xl border border-ink-200 bg-white/70 p-5 text-sm text-ink-600 leading-relaxed"
            >
              <span className="block text-[10px] uppercase tracking-[0.18em] text-primary font-semibold mb-2">
                Intellectual property note
              </span>
              <span className="italic">From Diagnosis to Goodbye</span> is
              the proprietary name of an educational and professional
              training platform developed and owned by Derrick Hall. A
              federal trademark application for the name is pending with
              the United States Patent and Trademark Office. The name
              identifies a coordinated series of books, workbooks,
              facilitator guides, participant materials, presentations,
              workshops, seminars, and professional training programs.
              Begin Again Living Ministries publishes, distributes, and
              hosts educational programming under license and permission
              from the author.
            </div>
          </div>
        </div>
      </section>

      {/* -------------- MEET OUR FOUNDER -------------- */}
      <section className="py-20 sm:py-28" data-testid="about-founder">
        <div className="container-x grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 reveal flex justify-center lg:justify-start">
            <div className="relative w-full max-w-[300px]">
              <div
                aria-hidden="true"
                className="absolute -inset-6 bg-gradient-to-br from-primary/15 via-secondary/15 to-transparent rounded-3xl blur-2xl"
              />
              <div className="relative rounded-3xl overflow-hidden border border-ink-100 shadow-[0_25px_70px_-25px_rgba(15,23,42,0.25)]">
                <img
                  src={AUTHOR.photo}
                  alt="Derrick Hall — founder of Begin Again Living Ministries"
                  width="300"
                  height="300"
                  className="block w-full h-auto"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-ink-900/85 via-ink-900/40 to-transparent px-5 py-4">
                  <div className="text-white">
                    <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-semibold text-secondary">
                      <span className="w-6 h-px bg-secondary" />
                      Founder
                    </div>
                    <div className="mt-1.5 font-heading text-xl leading-tight">
                      Derrick Hall
                    </div>
                    <div className="text-white/80 text-[11px] mt-0.5">
                      Hospice Chaplain &middot; Author &middot; Speaker &middot;
                      Grief Coach
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 reveal">
            <SectionHeading
              overline="Meet our founder"
              title="Ministry shaped at the bedside."
            />
            <div className="mt-6 space-y-5 text-ink-700 text-base sm:text-lg leading-relaxed">
              <p>
                Derrick Hall brings years of ministry and hospice experience to
                his work with patients, families, caregivers, grieving
                individuals, churches, and healthcare communities.
              </p>
              <p>
                His work is shaped not only by what he teaches, but by what he
                has witnessed &mdash; at bedsides, in conversations with
                families, through grief, and in the sacred moments when people
                need someone willing to listen and remain present.
              </p>
              <p>
                He is the founder of Begin Again Living Ministries and the
                author and creator of the{" "}
                <span className="font-semibold text-ink-900">
                  From Diagnosis to Goodbye
                </span>{" "}
                Educational Platform.
              </p>
            </div>

            <div className="mt-8 flex flex-wrap gap-2">
              {[
                "Hospice Chaplain",
                "Author",
                "Speaker",
                "Grief Coach",
                "Educator",
              ].map((role) => (
                <span
                  key={role}
                  className="inline-flex items-center text-[11px] uppercase tracking-[0.14em] px-3 py-1.5 rounded-full bg-softblue text-primary font-semibold"
                >
                  {role}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* -------------- THE HEART HASN'T CHANGED -------------- */}
      <section
        className="py-20 sm:py-28 bg-paper border-y border-ink-100"
        data-testid="about-heart"
      >
        <div className="container-x max-w-4xl text-center">
          <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 text-primary">
            <Path size={24} weight="duotone" />
          </span>
          <h2 className="mt-6 font-heading text-3xl sm:text-4xl lg:text-5xl font-medium text-ink-900 leading-[1.1]">
            The heart hasn&rsquo;t changed.
          </h2>
          <p className="mt-6 text-ink-700 text-lg leading-relaxed">
            Begin Again Living Ministries has grown considerably since that
            first $2,000 gift.
          </p>

          <div className="mt-10 grid sm:grid-cols-3 gap-4 max-w-2xl mx-auto text-left">
            {[
              { label: "The resources", detail: "are different." },
              { label: "The reach", detail: "is greater." },
              { label: "The vision", detail: "has expanded." },
            ].map((row) => (
              <div
                key={row.label}
                className="bg-white rounded-xl border border-ink-100 px-5 py-4 shadow-[0_4px_20px_rgba(0,0,0,0.03)]"
              >
                <div className="text-[10px] uppercase tracking-[0.18em] text-primary font-semibold">
                  {row.label}
                </div>
                <div className="mt-1.5 text-ink-900 font-medium">
                  {row.detail}
                </div>
              </div>
            ))}
          </div>

          <p className="mt-10 text-ink-700 text-lg leading-relaxed">
            But the heart of the ministry remains the same: helping people when
            they need somewhere to turn and reminding them that life&rsquo;s
            hardest chapter does not have to be the last word.
          </p>
        </div>
      </section>

      {/* -------------- CTA / CLOSING -------------- */}
      <section className="py-20 sm:py-28 bg-softblue/40" data-testid="about-cta">
        <div className="container-x text-center max-w-3xl mx-auto">
          <Sun size={30} weight="duotone" className="text-secondary mx-auto" />
          <h2 className="mt-6 font-heading text-3xl sm:text-5xl font-medium text-ink-900 leading-[1.1]">
            Walking with you, one step at a time.
          </h2>
          <div className="mt-6 space-y-3 text-ink-700 text-lg leading-relaxed">
            <p>
              However life has changed, you do not have to find your way
              forward alone.
            </p>
            <p className="text-ink-900 font-semibold">
              There is still life ahead. There is still hope. And there is
              still a place to begin again.
            </p>
          </div>

          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link
              to="/request-support"
              data-testid="about-request-btn"
              className="inline-flex items-center gap-2 bg-primary text-white rounded-full px-7 py-3.5 font-semibold hover:bg-primary-dark hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300"
            >
              Request Support <ArrowRight size={18} weight="bold" />
            </Link>
            <Link
              to="/donate"
              data-testid="about-partner-btn"
              className="inline-flex items-center gap-2 border-2 border-primary text-primary rounded-full px-7 py-3.5 font-semibold hover:bg-primary/5 transition-all duration-300"
            >
              <HandHeart size={18} weight="duotone" /> Partner With Us
            </Link>
          </div>

          <p
            data-testid="about-501c3"
            className="mt-12 max-w-3xl mx-auto text-sm text-ink-500 leading-relaxed border-t border-ink-100 pt-8"
          >
            Begin Again Living Ministries is a registered 501(c)(3) nonprofit
            organization dedicated to providing faith-based encouragement,
            grief support, spiritual care resources, and community support.
          </p>
        </div>
      </section>
    </div>
  );
};

export default About;
